import React, { useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { Tabs, Tab, Box } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import type { PageResponse } from "@/core/api/types/page";
import { isFormEndpoint, openFormModal } from "@/core/config/formRegistry";
import { isPageEndpoint, getPageNavigation } from "@/core/config/pageRegistry";
import { useDeleteMaterialeDidattico } from "@/modules/Didattica/MaterialeDidattico/hooks";
interface DynamicPageProps {
  data: PageResponse;
}

/**
 * Converte un endpoint get_page in endpoint form per l'edit
 * Es: "get_page/users/discenti" -> "form/users/discenti"
 * Es: "get_page/corsi/fad/1" -> "form/corsi/fad/1" (mantiene sub-tab)
 */
function getFormEndpointFromPage(pageEndpoint: string): string {
  if (pageEndpoint === "get_page/categorie_corsi") {
    return "form/categoria_corsi/0";
  }
  return pageEndpoint.replace("get_page/", "form/");
}

export default function DynamicPage({ data }: DynamicPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef<AgGridReact>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Hook per delete materiale didattico
  const deleteMaterialeDidattico = useDeleteMaterialeDidattico();

  // Handler delete per materiale didattico (passato al modale)
  const handleDeleteMaterialeDidattico = useCallback(async (id: string) => {
    await deleteMaterialeDidattico.mutateAsync(id);
  }, [deleteMaterialeDidattico]);

  // Tab principali (FAD, Aula, Misto, Webinar) - header level
  const primaryTabs = data.tabs || [];

  // Sub-tabs (Tutti, In programmazione, etc.) - body level
  const subTabs = data.body.tabs || [];

  // Tab principale attiva basata sull'URL
  // Usa startsWith per supportare endpoint interlacciati (es. /get_page/corsi/fad/2 matcha /get_page/corsi/fad)
  const activePrimaryTabIndex = useMemo(() => {
    const currentPath = location.pathname;
    const index = primaryTabs.findIndex((t) => {
      const tabRoute = `/${t.endpoint}`;
      return currentPath === tabRoute || currentPath.startsWith(tabRoute + "/");
    });
    return index >= 0 ? index : 0;
  }, [location.pathname, primaryTabs]);

  // Sub-tab attiva basata sull'URL - matcha con endpoint dal backend
  const activeSubTabIndex = useMemo(() => {
    const currentPath = location.pathname;
    const index = subTabs.findIndex((t) => t.endpoint && `/${t.endpoint}` === currentPath);
    return index >= 0 ? index : 0;
  }, [location.pathname, subTabs]);

  // Handler per click su cella edit
  const handleEditClick = useCallback((rowData: Record<string, any>) => {
    // Trova dinamicamente il primo campo che inizia con "id_"
    const idField = Object.keys(rowData).find(key => key.startsWith("id_"));
    const id = idField ? rowData[idField] : undefined;
    if (!id) {
      console.warn("No ID found in row data for edit", rowData);
      return;
    }

    // Converti l'endpoint corrente in form endpoint
    const currentEndpoint = location.pathname.replace("/", ""); // es. "get_page/users/discenti"
    const formEndpoint = getFormEndpointFromPage(currentEndpoint);
    const fullEndpoint = `${formEndpoint}/${id}`;

    // Prima controlla se è un endpoint pagina (es. corsi)
    if (isPageEndpoint(fullEndpoint)) {
      const nav = getPageNavigation(fullEndpoint, {
        data: rowData,
        returnTo: location.pathname,
      });
      if (nav) {
        navigate(nav.route, { state: nav.state });
      }
    } else {
      // Altrimenti apri la modale, passando anche il nome del campo ID
      const extraProps: Record<string, any> = { idField };
      if (fullEndpoint.includes('materiale_didattico')) {
        extraProps.onDelete = handleDeleteMaterialeDidattico;
      }
      openFormModal(fullEndpoint, extraProps);
    }
  }, [location.pathname, navigate, handleDeleteMaterialeDidattico]);

  // Mappa colonne API -> AG Grid ColDef
  const columnDefs: ColDef[] = useMemo(() => {
    const isCategorieCorsiPage = location.pathname.startsWith("/get_page/categorie_corsi");
    return Object.entries(data.body.columns).map(([field, col]) => {
      const baseDef: ColDef = {
        field,
        headerName: col.headerName,
        minWidth: col.width,
        flex: 1,
        sortable: col.sortable,
        filter: col.filterable,
        cellClass: col.alignment === "center" ? "ag-cell-center" :
                   col.alignment === "right" ? "ag-cell-right" : "ag-cell-left",
        headerClass: col.alignment === "center" ? "ag-header-center" :
                     col.alignment === "right" ? "ag-header-right" : "ag-header-left",
      };

      // Se type è html_edit, rendi la cella cliccabile e in grassetto
      const isCategoriaNameField =
        isCategorieCorsiPage && (field === "nome_categoria" || field === "descrizione");

      if (col.type === "html_edit" || isCategoriaNameField) {
        baseDef.cellRenderer = (params: ICellRendererParams) => (
          <span
            onClick={() => handleEditClick(params.data)}
            className="text-[#B80F15] font-bold cursor-pointer hover:underline"
          >
            {params.value}
          </span>
        );
      }

      return baseDef;
    });
  }, [data.body.columns, handleEditClick, location.pathname]);

  // Filtra dati per ricerca
  const filteredData = useMemo(() => {
    if (!searchTerm) return data.body.data;
    return data.body.data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data.body.data, searchTerm]);

  // Estrai info header
  const title = data.header.l.find((item) => item.enabled && item.title)?.title || "";
  const newButton = data.header.r.find((item) => item.enabled && item.label);
  const searchPlaceholder = data.header.r.find((item) => item.enabled && item.placeholder)?.placeholder;

  const handleSubTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = subTabs[newValue];
    if (tab?.endpoint) {
      navigate(`/${tab.endpoint}`);
    }
  };

  return (
    <div className="flex flex-col h-full gap-0">
      {/* Filters Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-0 pt-5 pb-0 bg-[#F3F3F3]">
          <div className="flex justify-between items-center px-8 box-border">
            {/* Title and counter */}
            <div className="flex gap-2 items-center">
              <h1 className="text-[24px] font-semibold text-gray-900">
                {title}
              </h1>
              <span className="ml-2 mt-1 bg-red-200 text-[#B80F15] py-1 px-2.5 rounded-full text-sm font-bold">
                {filteredData.length}
              </span>
            </div>

            {/* Search and New button */}
            <div className="flex gap-3 items-center flex-wrap">
              {newButton && (
                <button
                  className="bg-[#B80F15] rounded-[4px] text-white px-5 py-2 flex items-center gap-2 hover:bg-[#A00D12] transition-colors duration-200 text-sm font-medium"
                  onClick={() => {
                    if (newButton.endpoint) {
                      // Prima controlla se è un endpoint pagina (es. corsi)
                      if (isPageEndpoint(newButton.endpoint)) {
                        const nav = getPageNavigation(newButton.endpoint, {
                          returnTo: location.pathname,
                        });
                        if (nav) {
                          navigate(nav.route, { state: nav.state });
                        }
                      } else if (isFormEndpoint(newButton.endpoint)) {
                        // Altrimenti controlla se è un endpoint modale (es. users)
                        // Per materiale didattico, passa onDelete
                        const extraProps = newButton.endpoint.includes('materiale_didattico')
                          ? { onDelete: handleDeleteMaterialeDidattico }
                          : undefined;
                        openFormModal(newButton.endpoint, extraProps);
                      } else {
                        // Fallback: navigazione diretta
                        navigate(`/${newButton.endpoint}`);
                      }
                    }
                  }}
                >
                  <span>{newButton.label}</span>
                </button>
              )}
              {searchPlaceholder && (
                <div className="relative">
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    className="pl-4 pr-10 py-[9px] border text-sm border-gray-300 rounded-[4px] focus:outline-none focus:border-black focus:ring-black focus:ring-[0.5px] text-gray-500 placeholder-gray-600"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Primary Tabs (FAD, Aula, Misto, Webinar) - stile inline con separatore verticale */}
          {primaryTabs.length > 0 && (
            <div className="flex items-center pt-3 pb-2 mt-2 space-x-4 px-8">
              {primaryTabs.map((tab, index) => (
                <React.Fragment key={tab.id}>
                  <span
                    onClick={() => {
                      if (subTabs.length > 0) {
                        // Mantieni la sub-tab attuale: estrai suffisso dalla sub-tab corrente
                        const currentSubTab = subTabs[activeSubTabIndex];
                        const suffix = currentSubTab?.endpoint?.split('/').pop() || '0';
                        navigate(`/${tab.endpoint}/${suffix}`);
                      } else {
                        navigate(`/${tab.endpoint}`);
                      }
                    }}
                    className={`text-[20px] font-medium cursor-pointer ${
                      index === activePrimaryTabIndex
                        ? "text-[#B80F15]"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.name}
                  </span>
                  {index < primaryTabs.length - 1 && (
                    <div className="h-6 w-[1px] bg-gray-500"></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Sub-Tabs (Tutti, In programmazione, Pubblicato, Finito) */}
          {subTabs.length > 0 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, paddingBottom: 0 }}>
              <Tabs
                value={activeSubTabIndex}
                onChange={handleSubTabChange}
                variant="fullWidth"
                sx={{
                  outline: 'none',
                  border: 'none',
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#B80F15',
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    color: '#6B7280',
                    fontWeight: 500,
                    padding: '10px 0px',
                    textTransform: 'uppercase',
                    fontSize: '0.875rem',
                    minHeight: 48,
                    '&.Mui-selected': {
                      color: '#B80F15',
                      fontWeight: 600,
                      outline: 'none',
                      boxShadow: 'none',
                      border: 'none',
                    },
                    '&:hover': {
                      color: '#B80F15',
                      backgroundColor: 'rgba(184, 15, 21, 0.04)',
                    },
                    '&:active':{
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                    },
                    '&:focus':{
                      border: 'none',
                      boxShadow: 'none',
                      outline: 'none',
                    }
                  },
                }}
              >
                {subTabs.map((tab, index) => (
                  <Tab
                    key={tab.id}
                    label={tab.name}
                    value={index}
                  />
                ))}
              </Tabs>
            </Box>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-y-hidden flex-1 overflow-x-clip">
        <div className="w-full h-full rounded-none ag-theme-quartz">
          <AgGridReact
            ref={gridRef}
            domLayout="normal"
            autoSizeStrategy={{
              type: "fitGridWidth",
              defaultMinWidth: 100,
            }}
            pagination={false}
            paginationAutoPageSize={false}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            rowHeight={65}
            rowData={filteredData}
            columnDefs={columnDefs}
            overlayNoRowsTemplate="<span class='text-gray-500'>Nessun dato trovato</span>"
            onGridReady={(params) => {
              params.api.sizeColumnsToFit();
            }}
          />
        </div>
      </div>
    </div>
  );
}
